import React, { useState, useEffect } from 'react';
import { Button, Modal, Form } from 'react-bootstrap';
import Swal from 'sweetalert2';

import { SKU_MAPPING_WORKFLOW_SIMILARITY_THRESHOLD } from 'lib/networking/endpoints';
import useGet from 'lib/hooks/useGet';
import usePost from 'lib/hooks/usePost';

const SimilarityThresholdConfig = () => {
  // State for similarity threshold modal
  const [showThresholdModal, setShowThresholdModal] = useState(false);
  const [thresholdValue, setThresholdValue] = useState(0.9);

  // Get current similarity threshold
  const {
    data: { similarity_threshold: currentThreshold } = {},
    loading: thresholdLoading,
    refetch: refetchThreshold,
  } = useGet(SKU_MAPPING_WORKFLOW_SIMILARITY_THRESHOLD);

  // Update similarity threshold
  const { postData: updateThreshold, loading: updatingThreshold } = usePost(
    SKU_MAPPING_WORKFLOW_SIMILARITY_THRESHOLD,
    () => {
      setShowThresholdModal(false);
      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'Similarity threshold updated successfully',
      }).then(() => {
        refetchThreshold();
      });
    },
    error =>
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: `Failed to update threshold: ${error?.response?.data?.message || 'Unknown error'}`,
      }).then(() => {
        refetchThreshold();
      }),
  );

  // Update local state when current threshold is loaded
  useEffect(() => {
    if (currentThreshold !== undefined) {
      setThresholdValue(currentThreshold);
    }
  }, [currentThreshold]);

  const handleThresholdSubmit = e => {
    e.preventDefault();
    const threshold = parseFloat(thresholdValue);

    if (isNaN(threshold) || threshold < 0 || threshold > 2) {
      Swal.fire({
        icon: 'error',
        title: 'Invalid Input',
        text: 'Similarity threshold must be a number between 0 and 2',
      });
      return;
    }

    updateThreshold({
      similarity_threshold: threshold,
    });
  };

  return (
    <>
      <Button
        variant="outline-primary"
        size="sm"
        onClick={() => setShowThresholdModal(true)}
        disabled={thresholdLoading}
        className="mr-2"
      >
        {thresholdLoading ? 'Loading...' : `Threshold (${(currentThreshold || 0.9).toFixed(2)})`}
      </Button>

      {/* Similarity Threshold Modal */}
      <Modal show={showThresholdModal} onHide={() => setShowThresholdModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Similarity Threshold Configuration</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleThresholdSubmit}>
          <Modal.Body>
            <Form.Group>
              <Form.Label>
                Similarity Threshold (
                {currentThreshold ? 'Current: ' + currentThreshold.toFixed(2) : 'Default: 0.90'})
              </Form.Label>
              <Form.Control
                type="number"
                min="0"
                max="2"
                step="0.01"
                value={thresholdValue}
                onChange={e => setThresholdValue(e.target.value)}
                placeholder="Enter threshold (0.0 - 2.0)"
                required
              />
              <Form.Text className="text-muted">
                Products with similarity scores above this threshold will be automatically linked.
                Lower values create more matches but may be less accurate. Higher values are more
                accurate but may miss potential matches.
              </Form.Text>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => setShowThresholdModal(false)}
              disabled={updatingThreshold}
            >
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={updatingThreshold}>
              {updatingThreshold ? 'Updating...' : 'Update Threshold'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  );
};

export default SimilarityThresholdConfig;
